// src/pages/purchases/PurchaseList.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format, parseISO, isAfter, isBefore, startOfDay, endOfDay, subDays } from "date-fns";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../utils/config";

export default function PurchaseList() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // search & filters
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");

  // sorting
  const [sortBy, setSortBy] = useState("date"); // 'date' | 'amount' | 'id'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  // fetch
  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/api/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPurchases(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);
        setErr("Failed to load purchases");
      } finally {
        setLoading(false);
      }
    };
    fetchPurchases();
  }, []);

  // debounced search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const formatINR = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(
      Number(n || 0)
    );

  const gross = (p) => Number(p.total_amount || 0);
  const invDate = (p) => {
    // support ISO strings or 'yyyy-MM-dd'/'dd-MM-yyyy'
    try {
      return p.invoice_date ? (/\d{4}-\d{2}-\d{2}/.test(p.invoice_date) ? parseISO(p.invoice_date) : new Date(p.invoice_date)) : null;
    } catch {
      return null;
    }
  };

  // suppliers list for dropdown
  const suppliers = useMemo(() => {
    const set = new Set();
    purchases.forEach((p) => p.supplier_name && set.add(p.supplier_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [purchases]);

  // filtering
  const filtered = useMemo(() => {
    let list = purchases.slice();

    if (debouncedQ) {
      const ql = debouncedQ.toLowerCase();
      list = list.filter(
        (p) =>
          String(p.id).includes(ql) ||
          (p.supplier_name || "").toLowerCase().includes(ql) ||
          (p.invoice_number || "").toLowerCase().includes(ql)
      );
    }

    if (supplierFilter) {
      list = list.filter((p) => p.supplier_name === supplierFilter);
    }

    if (dateFrom) {
      const from = startOfDay(new Date(dateFrom));
      list = list.filter((p) => {
        const d = invDate(p);
        return d ? isAfter(d, subDays(from, 1)) : false;
      });
    }
    if (dateTo) {
      const to = endOfDay(new Date(dateTo));
      list = list.filter((p) => {
        const d = invDate(p);
        return d ? isBefore(d, to) : false;
      });
    }

    // sorting
    list.sort((a, b) => {
      let va = 0,
        vb = 0;
      if (sortBy === "amount") {
        va = gross(a);
        vb = gross(b);
      } else if (sortBy === "id") {
        va = Number(a.id || 0);
        vb = Number(b.id || 0);
      } else {
        // date
        const da = invDate(a)?.getTime() || 0;
        const db = invDate(b)?.getTime() || 0;
        va = da;
        vb = db;
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });

    return list;
  }, [purchases, debouncedQ, dateFrom, dateTo, supplierFilter, sortBy, sortDir]);

  const totals = useMemo(() => {
    const count = filtered.length;
    const totalAmount = filtered.reduce((s, p) => s + gross(p), 0);
    const totalGST = filtered.reduce((s, p) => s + Number(p.total_gst || 0), 0);
    const avg = count ? totalAmount / count : 0;
    return { count, totalAmount, totalGST, avg };
  }, [filtered]);

  const setQuickRange = (days) => {
    const to = new Date();
    const from = subDays(to, days - 1);
    setDateFrom(format(from, "yyyy-MM-dd"));
    setDateTo(format(to, "yyyy-MM-dd"));
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Purchase List</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search supplier / invoice / id…"
              className="w-72 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                title="Clear"
              >
                ✕
              </button>
            )}
          </div>

          <Link
            to="/purchases/add"
            className="hidden sm:inline-flex rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 active:scale-[.98]"
          >
            + Add Purchase
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Supplier</label>
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm bg-white"
          >
            <option value="">All</option>
            {suppliers.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setQuickRange(7)}
            className="rounded-full border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Last 7d
          </button>
          <button
            onClick={() => setQuickRange(30)}
            className="rounded-full border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            30d
          </button>
          <button
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setSupplierFilter("");
            }}
            className="ml-auto rounded-full border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="text-xs text-slate-500">Entries</div>
          <div className="text-lg font-semibold">{totals.count}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="text-xs text-slate-500">Total Amount (incl. GST)</div>
          <div className="text-lg font-semibold tabular-nums">{formatINR(totals.totalAmount)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="text-xs text-slate-500">Total GST</div>
          <div className="text-lg font-semibold tabular-nums">{formatINR(totals.totalGST)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="text-xs text-slate-500">Avg Invoice</div>
          <div className="text-lg font-semibold tabular-nums">{formatINR(totals.avg)}</div>
        </div>
      </div>

      {/* Errors */}
      {err && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}

      {/* Desktop / Tablet Table */}
      <div className="hidden sm:block overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
              <tr>
                <th
                  className="p-3 text-left font-medium cursor-pointer select-none"
                  onClick={() => toggleSort("id")}
                  title="Sort by ID"
                >
                  ID {sortBy === "id" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </th>
                <th className="p-3 text-left font-medium">Supplier</th>
                <th className="p-3 text-left font-medium">Invoice No</th>
                <th
                  className="p-3 text-left font-medium cursor-pointer select-none"
                  onClick={() => toggleSort("date")}
                  title="Sort by Date"
                >
                  Date {sortBy === "date" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </th>
                <th
                  className="p-3 text-right font-medium cursor-pointer select-none"
                  onClick={() => toggleSort("amount")}
                  title="Sort by Amount"
                >
                  Total Amount {sortBy === "amount" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </th>
                <th className="p-3 text-right font-medium">Created By</th>
                <th className="p-3 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-gray-50">
              {loading && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500">
                    No purchases match the current filters.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((p) => {
                  const d = invDate(p);
                  return (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/70">
                      <td className="p-3">{p.id}</td>
                      <td className="p-3">{p.supplier_name}</td>
                      <td className="p-3">{p.invoice_number}</td>
                      <td className="p-3">{d ? format(d, "dd-MM-yyyy") : "-"}</td>
                      <td className="p-3 text-right tabular-nums">{formatINR(gross(p))}</td>
                      <td className="p-3 text-right">{p.created_by}</td>
                      <td className="p-3">
                        <Link to={`/purchases/${p.id}`} className="text-blue-600 hover:underline">
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-3">
        {loading && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-center text-gray-500">
            Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-center text-gray-500">
            No purchases match the current filters.
          </div>
        )}
        {!loading &&
          filtered.map((p) => {
            const d = invDate(p);
            return (
              <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-slate-500">#{p.id}</div>
                    <div className="font-medium text-slate-900">{p.supplier_name}</div>
                    <div className="mt-1 text-xs text-slate-600">Invoice: {p.invoice_number || "-"}</div>
                    <div className="text-xs text-slate-600">Date: {d ? format(d, "dd-MM-yyyy") : "-"}</div>
                    <div className="text-xs text-slate-600">By: {p.created_by}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Amount</div>
                    <div className="text-lg font-semibold tabular-nums">{formatINR(gross(p))}</div>
                    <Link to={`/purchases/${p.id}`} className="mt-2 inline-block text-blue-600">
                      View
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Mobile FAB */}
      <Link
        to="/purchases/add"
        className="fixed bottom-5 right-5 h-12 w-12 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/30 active:scale-95 sm:hidden grid place-items-center"
        aria-label="Add Purchase"
      >
        +
      </Link>
    </div>
  );
}
